import { useNavigate } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  StatusChip,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { formatDate, formatDateTime, personLabel } from "../format";
import { formatLabel } from "../labels";
import type { CommunityOverview } from "../types";

/**
 * What Play stores about a community, for the people who govern it: who runs
 * it, who is in it, how people join, what was reported, and how active it is.
 * Every number comes from the overview endpoint, which counts real rows; an
 * unavailable slice says so rather than showing zero.
 */
export function CommunityOverviewPanel({
  overview,
  onRemove,
}: {
  overview: CommunityOverview;
  /** Takes someone out of the community in the app. Absent for HQ-made communities. */
  onRemove?: (person: { userId: string; label: string }) => void;
}) {
  const navigate = useNavigate();
  const { counts, profile, join, roster, reports, recent_posts: posts } = overview;

  const stat = (label: string, value: number | string | null, hint?: string) => (
    <Card>
      <Stack gap={4}>
        <Text role="caption" color="muted" as="div">
          {label}
        </Text>
        <Text role="h2" as="div">
          {value === null ? "—" : value}
        </Text>
        {hint ? (
          <Text role="caption" color="muted" as="div">
            {hint}
          </Text>
        ) : null}
      </Stack>
    </Card>
  );

  const openReports = reports.filter((r) => r.status !== "resolved" && r.status !== "closed");

  return (
    <Stack gap={16}>
      {overview.source === "hq" ? (
        <Alert tone="info" title="Created in HQ">
          This community has no Play community behind it, so there is no roster, chat or joining
          setup to show. Communities players create in the app appear here automatically.
        </Alert>
      ) : null}

      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
        }}
      >
        {stat("Members", counts.members, `${counts.joined_last_7_days} joined in the last 7 days`)}
        {stat("Admins", counts.admins)}
        {stat("Posts", counts.posts, counts.last_post_at ? `Last ${formatDate(counts.last_post_at)}` : "No posts yet")}
        {stat(
          "Chat messages",
          counts.messages,
          counts.messages_last_7_days === null ? undefined : `${counts.messages_last_7_days} in the last 7 days`,
        )}
        {stat(
          "Open reports",
          counts.open_reports,
          counts.moderation_cases === null ? undefined : `${counts.moderation_cases} moderation case${counts.moderation_cases === 1 ? "" : "s"}`,
        )}
        {stat(
          "Removed by moderation",
          counts.messages_removed_by_moderation,
          counts.posts_pending_review > 0 ? `${counts.posts_pending_review} posts awaiting review` : undefined,
        )}
      </div>

      <Surface>
        <Tabs
          items={[
            {
              id: "people",
              label: `People (${roster.total})`,
              content: (
                <Stack gap={12}>
                  <Text role="body" as="p">
                    Owner: {overview.owner ? personLabel(overview.owner) : "—"}
                  </Text>
                  {roster.total === 0 ? (
                    <EmptyState
                      title="No members"
                      description="Nobody has joined yet. Members appear here when players join in the app."
                    />
                  ) : (
                    <>
                      <Table>
                        <Thead>
                          <Tr>
                            <Th>Person</Th>
                            <Th>Role</Th>
                            <Th>Joined</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {roster.people.map((p) => (
                            <Tr key={p.user_id}>
                              <Td>{personLabel(p)}</Td>
                              <Td>
                                <StatusChip tone={p.role === "Founder" ? "info" : p.role === "Admin" ? "success" : "neutral"}>
                                  {p.role}
                                </StatusChip>
                              </Td>
                              <Td>{formatDate(p.joined_at)}</Td>
                              <Td>
                                <Flex gap={4} wrap>
                                  <Button size="sm" variant="secondary" onClick={() => void navigate(`/app/users/${p.user_id}`)}>
                                    Open user
                                  </Button>
                                  {onRemove && p.role !== "Founder" ? (
                                    <Button
                                      size="sm"
                                      variant="danger"
                                      onClick={() => onRemove({ userId: p.user_id, label: personLabel(p) })}
                                    >
                                      Remove
                                    </Button>
                                  ) : null}
                                </Flex>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                      {roster.total > roster.shown ? (
                        <Text role="caption" color="muted" as="p">
                          Showing the founder, admins and the first {roster.shown} of {roster.total} people.
                        </Text>
                      ) : null}
                    </>
                  )}
                </Stack>
              ),
            },
            {
              id: "joining",
              label: "Joining",
              content: !join ? (
                <EmptyState title="No joining setup" description="Only communities created in the app have joining rules." />
              ) : (
                <Stack gap={12}>
                  <Flex gap={8} wrap>
                    <StatusChip tone={join.mode === "approval" ? "warning" : "success"}>
                      {join.mode === "approval" ? "Approval required" : "Open to anyone"}
                    </StatusChip>
                    {profile ? (
                      <StatusChip tone={profile.discoverable ? "success" : "neutral"}>
                        {profile.discoverable ? "Discoverable" : "Hidden from search"}
                      </StatusChip>
                    ) : null}
                    {profile?.requires_post_approval ? <StatusChip tone="info">Posts need approval</StatusChip> : null}
                  </Flex>
                  <Text role="title" as="h3">
                    Questions asked of applicants
                  </Text>
                  {join.questions === null ? (
                    <Text role="caption" color="muted" as="p">
                      This database has no join-question tables yet.
                    </Text>
                  ) : join.questions.length === 0 ? (
                    <Text role="caption" color="muted" as="p">
                      {join.mode === "approval"
                        ? "No questions set. The founder can add them in the app."
                        : "None. Anyone can join without answering."}
                    </Text>
                  ) : (
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      {join.questions.map((q) => (
                        <li key={q.position}>
                          <Text role="body" as="span">
                            {q.prompt}
                          </Text>{" "}
                          <Text role="caption" color="muted" as="span">
                            {q.is_agreement ? "Agreement" : "Free text"} · {q.required ? "Required" : "Optional"}
                          </Text>
                        </li>
                      ))}
                    </ol>
                  )}
                  <Text role="title" as="h3">
                    Requests
                  </Text>
                  {join.requests === null ? (
                    <Text role="caption" color="muted" as="p">
                      This database has no join-request tables yet.
                    </Text>
                  ) : (
                    <>
                      <Text role="body" as="p">
                        {join.requests.pending} waiting · {join.requests.approved} approved · {join.requests.rejected} declined
                      </Text>
                      {join.requests.recent_pending.length > 0 ? (
                        <Table>
                          <Thead>
                            <Tr>
                              <Th>Applicant</Th>
                              <Th>Asked</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {join.requests.recent_pending.map((r) => (
                              <Tr key={r.id}>
                                <Td>{personLabel(r)}</Td>
                                <Td>{formatDateTime(r.created_at)}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      ) : null}
                      <Text role="caption" color="muted" as="p">
                        The founder and admins decide requests in the app. Applicants' answers stay with them.
                      </Text>
                    </>
                  )}
                </Stack>
              ),
            },
            {
              id: "reports",
              label: `Reports (${openReports.length} open)`,
              content:
                reports.length === 0 ? (
                  <EmptyState
                    title="No reports"
                    description="Nobody has reported this community. Reports filed in the app land in Trust & Safety and are listed here."
                  />
                ) : (
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Type</Th>
                        <Th>Status</Th>
                        <Th>Submitted</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {reports.map((r) => (
                        <Tr key={r.id}>
                          <Td>{formatLabel(r.report_type)}</Td>
                          <Td>
                            <StatusChip tone={r.status === "resolved" || r.status === "closed" ? "neutral" : "warning"}>
                              {formatLabel(r.status)}
                            </StatusChip>
                          </Td>
                          <Td>{formatDateTime(r.submitted_at)}</Td>
                          <Td>
                            <Flex gap={4} wrap>
                              <Button size="sm" variant="secondary" onClick={() => void navigate(`/app/trust-safety/reports/${r.id}`)}>
                                Open report
                              </Button>
                              {r.moderation_case_id ? (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => void navigate(`/app/trust-safety/cases/${r.moderation_case_id}`)}
                                >
                                  Open case
                                </Button>
                              ) : null}
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ),
            },
            {
              id: "activity",
              label: "Activity",
              content:
                posts.length === 0 ? (
                  <EmptyState title="No posts yet" description="Posts members make in the app appear here." />
                ) : (
                  <Table>
                    <Thead>
                      <Tr>
                        <Th>Post</Th>
                        <Th>Author</Th>
                        <Th>Posted</Th>
                        <Th>State</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {posts.map((p) => (
                        <Tr key={p.id}>
                          <Td>
                            {p.title ?? "(untitled)"}
                            {p.kind ? (
                              <Text role="caption" color="muted" as="div">
                                {formatLabel(p.kind)}
                              </Text>
                            ) : null}
                          </Td>
                          <Td>{personLabel(p.author)}</Td>
                          <Td>{formatDateTime(p.created_at)}</Td>
                          <Td>
                            <Flex gap={4} wrap>
                              {p.pinned ? <StatusChip tone="info">Pinned</StatusChip> : null}
                              {p.pending_review ? <StatusChip tone="warning">Awaiting review</StatusChip> : null}
                              {!p.pinned && !p.pending_review ? "—" : null}
                            </Flex>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                ),
            },
            {
              id: "profile",
              label: "Profile",
              content: !profile ? (
                <EmptyState title="No Play profile" description="Created in HQ, so there is no profile set in the app." />
              ) : (
                <Stack gap={8}>
                  <Text role="body" as="p">
                    Handle: {profile.handle ? `@${profile.handle}` : "—"}
                  </Text>
                  <Text role="body" as="p">
                    Sports: {profile.sports.length > 0 ? profile.sports.join(", ") : "—"}
                  </Text>
                  <Text role="body" as="p">
                    Topics: {profile.topics.length > 0 ? profile.topics.join(", ") : "—"}
                  </Text>
                  <Text role="body" as="p">
                    Created: {formatDateTime(profile.created_at)}
                  </Text>
                  <Text role="body" as="p">
                    Archived in the app: {profile.archived ? "Yes" : "No"}
                  </Text>
                  <Text role="title" as="h3">
                    Rules
                  </Text>
                  <Text role="body" as="p" style={{ whiteSpace: "pre-wrap" }}>
                    {profile.rules?.trim() ? profile.rules : "No rules written."}
                  </Text>
                </Stack>
              ),
            },
          ]}
        />
      </Surface>
    </Stack>
  );
}
